import Konva from "konva";
import type {AreaEffect} from "@/encounter/model/AreaEffect.ts";
import {useAreaEffect} from "@/encounter/effect/AreaEffectHooks.ts";
import {useTableTopInteraction} from "@/tabletop/TableTopInteractionHooks.ts";
import {
    type AreaEffectResizeDimension,
    resizeAreaEffect,
    toAreaEffectLocalPosition,
} from "@/tabletop/effects/AreaEffectGeometry.ts";
import type Position from "@/util/Position.ts";
import {getAngleDegrees, normalizeDegrees, snapDegrees} from "@/util/Rotation.ts";
import {
    type TransformHistoryTarget,
    type TransformSnapshot,
    useTableTopHistory,
} from "@/tabletop/history/TableTopHistoryHooks.ts";

const ROTATION_SNAP_STEP = 15;

export function useAreaEffectHandleInteraction(
    effect: AreaEffect,
    origin: Position,
    rotation: number,
) {
    const {updateEffect} = useAreaEffect();
    const {trackWorldPointer} = useTableTopInteraction();
    const {beginInteraction, endInteraction} = useTableTopHistory();

    const claimHandleInteraction = (event: Konva.KonvaEventObject<MouseEvent>) => {
        // A handle owns its gesture and must not trigger the effect or a token below it.
        event.cancelBubble = true;
        event.evt.preventDefault();
        return event.evt.button === 0;
    };

    const startHandleInteraction = (
        event: Konva.KonvaEventObject<MouseEvent>,
        session: {
            onChange: (pointer: Position, mouseEvent: MouseEvent) => void;
            onEnd: (pointer: Position, mouseEvent: MouseEvent) => TransformSnapshot | undefined;
            historyTarget?: TransformHistoryTarget;
        },
    ) => {
        if (!claimHandleInteraction(event)) return;

        const stage = event.target.getStage();
        if (!stage) return;
        const started = trackWorldPointer(stage, {
            requireMovement: true,
            onCancel: () => endInteraction(),
            onChange: session.onChange,
            onEnd: (pointer, mouseEvent) => endInteraction(session.onEnd(pointer, mouseEvent)),
        });
        if (started) beginInteraction(session.historyTarget);
    };

    const startResize = (
        dimension: AreaEffectResizeDimension,
        event: Konva.KonvaEventObject<MouseEvent>,
    ) => {
        const getResizedEffect = (pointer: Position) => {
            const localPointer = toAreaEffectLocalPosition(pointer, origin, rotation);
            return resizeAreaEffect(effect, dimension, localPointer);
        };
        startHandleInteraction(event, {
            onChange: pointer => updateEffect(getResizedEffect(pointer), "debounced"),
            onEnd: pointer => {
                updateEffect(getResizedEffect(pointer), "immediate");
                return undefined;
            },
        });
    };

    const startRotation = (event: Konva.KonvaEventObject<MouseEvent>) => {
        const getRotatedEffect = (pointer: Position, mouseEvent: MouseEvent): AreaEffect => {
            const pointerRotation = getAngleDegrees(origin, pointer);
            return {
                ...effect,
                rotation: mouseEvent.ctrlKey
                    ? snapDegrees(pointerRotation, ROTATION_SNAP_STEP)
                    : normalizeDegrees(pointerRotation),
            };
        };
        startHandleInteraction(event, {
            onChange: (pointer, mouseEvent) =>
                updateEffect(getRotatedEffect(pointer, mouseEvent), "debounced"),
            onEnd: (pointer, mouseEvent) => {
                const rotatedEffect = getRotatedEffect(pointer, mouseEvent);
                updateEffect(rotatedEffect, "immediate");
                return rotatedEffect;
            },
            historyTarget: {type: "areaEffect", id: effect.id},
        });
    };

    const toggleLock = (event: Konva.KonvaEventObject<MouseEvent>) => {
        if (!claimHandleInteraction(event)) return;
        updateEffect({...effect, locked: !effect.locked});
    };

    return {startResize, startRotation, toggleLock};
}
